import { Tag, FormInstance } from "antd";
import { QueryOperation, SearchQuery } from "../../code/search-query/search-query";
import {
  buildSearchQuery,
  comboboxValue,
  dateRangeValue,
  QueryFieldMapping,
} from "../../code/search-query/query-mapper";
import { DataColumn } from "../../controls/Data.default";
import { DynamicSchema } from "../../controls/DynamicForm.default";
import { InputObject } from "../../controls/default";
import { Dayjs } from "dayjs";
import {
  createSearchRequest,
  createScopedFilterRequest,
} from "../../hooks/useSearchRequest";
import {
  getPlatformUsers,
  getRewards,
  getCompanies,
  getCompanyDepartments,
} from "../../repository/data.repository";
import { RewardRedemptionStatus } from "../../repository/data.default";
import { createStatusColorMap } from "../../utils/colors";

export interface RewardRedemptionsForm {
  readonly user: InputObject;
  readonly reward: InputObject;
  readonly status: InputObject;
  readonly company: InputObject;
  readonly department: InputObject;
  readonly requestedOn: Dayjs[];
}

// Shared by the grid column and the manage dialog so the status colour stays consistent.
export const redemptionStatusColor = createStatusColorMap<string>({
  [RewardRedemptionStatus.Pending]: "gold",
  [RewardRedemptionStatus.Approved]: "blue",
  [RewardRedemptionStatus.Fulfilled]: "green",
  [RewardRedemptionStatus.Rejected]: "red",
  [RewardRedemptionStatus.Cancelled]: "default",
});

export const redemptionStatusOptions = Object.values(RewardRedemptionStatus).map(
  (status) => ({ display: status, value: status }),
);

// A work-queue: default to the items that need action so the grid is useful on open.
export const redemptionInitialValues = {
  status: {
    value: RewardRedemptionStatus.Pending,
    display: RewardRedemptionStatus.Pending,
  },
};

export const rewardRedemptionsViewColumns: DataColumn[] = [
  { title: "User", key: "userUserName", width: 168, sort: true, defaultSort: "asc", fixed: "left" },
  { title: "Reward", key: "rewardName", width: 168, sort: true },
  { title: "Points", key: "pointsSpent", width: 100, sort: true, type: "badge" },
  {
    title: "Status",
    key: "status",
    width: 120,
    sort: true,
    transform: (value: string) => (
      <Tag color={redemptionStatusColor(value)}>{value}</Tag>
    ),
  },
  {
    title: "Requested On",
    key: "requestedOn",
    width: 168,
    sort: true,
    format: "DD/MM/YYYY HH:mm",
  },
  {
    title: "Decided On",
    key: "decidedOn",
    width: 168,
    sort: true,
    format: "DD/MM/YYYY HH:mm",
  },
  {
    title: "Decided By",
    key: "decidedByUserUserName",
    width: 168,
    sort: true,
  },
  { title: "Notes", key: "notes", width: 240, sort: true },
];

const redemptionQueryMappings: QueryFieldMapping[] = [
  { formField: "user", queryField: "userUid", operation: QueryOperation.Equal, transform: comboboxValue },
  { formField: "reward", queryField: "rewardUid", operation: QueryOperation.Equal, transform: comboboxValue },
  { formField: "status", operation: QueryOperation.Equal, transform: comboboxValue },
  { formField: "company", queryField: "companyUid", operation: QueryOperation.Equal, transform: comboboxValue },
  { formField: "department", queryField: "departmentUid", operation: QueryOperation.Equal, transform: comboboxValue },
  { formField: "requestedOn", operation: QueryOperation.Between, transform: dateRangeValue },
];

export const mapRewardRedemptionsViewQuery = (
  values: RewardRedemptionsForm,
): SearchQuery => buildSearchQuery(values, redemptionQueryMappings);

// Factory so User / Department dropdowns narrow by the chosen Company / Department (soft scope; `dependsOn`
// clears stale children). Status keeps its static options; the queue still defaults to Pending via autoSearch.
export const rewardRedemptionsSearchSchema = (
  form: FormInstance<any>,
): DynamicSchema => ({
  groups: [
    {
      controls: [
        {
          type: "combobox",
          name: "company",
          label: "Company",
          request: createSearchRequest(getCompanies, "name"),
        },
        {
          type: "combobox",
          name: "department",
          label: "Department",
          dependsOn: ["company"],
          request: createScopedFilterRequest(getCompanyDepartments, "name", () => [
            ["companyUid", form.getFieldValue("company")?.value],
          ]),
        },
        {
          type: "combobox",
          name: "user",
          label: "User",
          dependsOn: ["company", "department"],
          request: createScopedFilterRequest(getPlatformUsers, "userName", () => [
            ["companyUid", form.getFieldValue("company")?.value],
            ["departmentUid", form.getFieldValue("department")?.value],
          ]),
        },
        {
          type: "combobox",
          name: "reward",
          label: "Reward",
          request: createSearchRequest(getRewards, "name"),
        },
        {
          type: "combobox",
          name: "status",
          label: "Status",
          request: () => Promise.resolve(redemptionStatusOptions),
        },
        { type: "rangetimepicker", name: "requestedOn", label: "Requested On" },
      ],
    },
  ],
});
